import {
  Alert,
  Badge,
  Button,
  FileInput,
  Group,
  NumberInput,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  EKIPMAN_ICE_AKTARMA_SONUC_ETIKETI,
  EKIPMAN_KONTROL_DURUMU_ETIKETI,
  EKIPMAN_SAHIPLIGI_ETIKETI,
  type EkipmanKontrolDurumu,
} from '@isg/validation';
import {
  BOS_EKIPMAN_SUZGECI,
  ekipmanSuzgecSorgusu,
  etkinEkipmanSuzgeciSayisi,
  useCiktiIndir,
  useCompanies,
  useEkipmanEkle,
  useEkipmanEnvanteri,
  useEkipmanPeriyotOnBilgi,
  useEkipmanYukle,
  useLookup,
  useWorkplacesTumu,
  type EkipmanSuzgeci,
  useKapilar,
} from '../api/queries';
import { useState } from 'react';
import { Select } from '@mantine/core';
import { tarihGoster } from '../lib/format';
import { BosListeNotu, QueryStatus } from './parts';
import { SatirEylemleri, Sayfalama, SolukSatir, SuzgecGostergesi, KapiSebebi } from '../sozlesme';
import { useNavigate, useSearchParams } from 'react-router';
import { ANLAM } from '../theme';

/**
 * Ekipman listesi (F1·S10): işyerleri üzerinden düzleştirilmiş envanter.
 *
 * 1 GÜN MARJININ GÖRÜNÜR YÜZÜ (Kapsam §16.5): sonraki_kontrol ≤ yarın olan
 * ekipman izinde SEÇİLEMEZ — rozet bunu şimdiden söyler. Kontrol tarihi
 * bilinmeyen ekipmana sistem "uygundur" diyemez — o da ayrı rozet taşır.
 *
 * ✏️ **TUR 4 ARTÇISI / Y3 — SÜZGEÇ VE SAYFALAMA SUNUCUYA TAŞINDI.** Ekran
 * işyeri başına paralel sorgu atıp bahçenin tamamını tarayıcıda birleştiriyor,
 * sonra kendi içinde süzüp sayfalıyordu; envanter modülün **en kalabalık**
 * listesidir ve bu, süzgeci de sayfalamayı da *"her şey indikten sonra"*
 * çalışan bir süse çeviriyordu. Artık tek uç (`GET /api/equipment`), beş
 * süzgeç ve sunucudan gelen **gerçek** toplam.
 *
 * ⚠️ **ÇIKTI EKRANIN SORGUSUNU TEKRAR KOŞAR (P4):** liste ve Excel aynı
 * sorgu dizgisini `ekipmanSuzgecSorgusu`tan kurar — ikisi ayrı yazılsaydı
 * *"ekranda 12 vardı, dosyada 9 çıktı"* farkı kimse bakmadan doğardı.
 *
 * ✏️ **Y2 — HIZLI GİRİŞ SOLUK SATIRDA** (madde 145). Modal KALKTI: envanter
 * tek tek girilirken modal açıp kapamak, on beş kalemde eli klavyeden
 * kaldırır (P1-a'nın kendi gerekçesi). ⚠️ **P1'in *"kendi sayfası olan kayıt
 * ayrı sayfada açılır"* kuralı DELİNMEDİ** — soluk satır kaydın EVİ değil
 * GİRİŞ NOKTASIDIR; künye kaydın kendi sayfasında tamamlanır (Y1) ve o sayfa
 * bu turda açıldı.
 *
 * ⚠️ **SÜRE ALANI KAYDA GİTMEZ (91-1):** işyeri × tip satırına gider ve o
 * işyerindeki AYNI TİPTEKİ TÜM ekipmanı etkiler — satır bunu, süre zaten
 * tanımlıysa **SORMAYARAK** söyler.
 */

const DURUM_SUZGECI = [
  { value: 'hepsi', label: 'Tüm kontrol durumları' },
  { value: 'marj', label: EKIPMAN_KONTROL_DURUMU_ETIKETI.marj },
  { value: 'yaklasiyor', label: EKIPMAN_KONTROL_DURUMU_ETIKETI.yaklasiyor },
  { value: 'bilinmiyor', label: EKIPMAN_KONTROL_DURUMU_ETIKETI.bilinmiyor },
  { value: 'gecerli', label: EKIPMAN_KONTROL_DURUMU_ETIKETI.gecerli },
] as const;

const EKIPMAN_DURUM_SUZGECI = [
  { value: 'hepsi', label: 'Aktif + pasif' },
  { value: 'aktif', label: 'Aktif' },
  { value: 'pasif', label: 'Pasif' },
] as const;

const SAHIPLIK_SUZGECI = [
  { value: 'hepsi', label: 'Tüm sahiplikler' },
  { value: 'kendi', label: EKIPMAN_SAHIPLIGI_ETIKETI.kendi },
  { value: 'yuklenici', label: EKIPMAN_SAHIPLIGI_ETIKETI.yuklenici },
] as const;

/** Sunucunun varsayılanıyla AYNI olmak zorunda (`listPaged` sayfaBoyutu). */
const SAYFA_BOYUTU = 25;
const KOLON_SAYISI = 8;

export function EquipmentScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const companies = useCompanies();
  // ✏️ **TUR 9 / B4 (T9-18·T9-19): TEK İSTEK.** Şirket başına fan-out
  // (`useWorkplacesOf`) yerine bahçe filtreli tek uç; *"her sorgu başarılı
  // mı"* (every) ve *"hepsini birleştir"* (flatMap) desenleri de düştü —
  // istemcide birleştirilen bir listede **bir isteğin sessizce boş dönmesi
  // hiçbir yerde görünmüyordu** (madde 180'in sınıfı).
  const isyerleri = useWorkplacesTumu();
  const workplaces = isyerleri.data;
  const tipler = useLookup('equipment_tip');
  const indir = useCiktiIndir();

  // ACİL DURUM / E3 (Kapsam §29): acil durum sayfasının bağlantısı süzgeci
  // URL'den ön doldurur (`?workplaceId=…&acilDurum=true`) — ikinci envanter
  // yok, aynı liste süzülür.
  const [arama] = useSearchParams();
  const [suzgec, setSuzgec] = useState<EkipmanSuzgeci>(() => ({
    ...BOS_EKIPMAN_SUZGECI,
    workplaceId: arama.get('workplaceId'),
    acilDurum: arama.get('acilDurum') === 'true' ? true : null,
  }));
  const envanter = useEkipmanEnvanteri(suzgec);
  // P13-b (madde 225): iki kapı SUNUCUDAN (`GET kapilar`) — ekipman yazma
  // `EKIPMAN_YAZMA_SAPKALARI` · toplu yükleme yalnız holding yön. (Y4). Bahçe,
  // işyeri seçilince kaydın kendi kapısında koşar (§2.16).
  const kapilar = useKapilar();
  const yazabilir = kapilar.data?.ekipmanKapisi.yazabilir ?? false;
  const holdingYoneticisi = kapilar.data?.ekipmanYuklemeKapisi.yazabilir ?? false;

  const [yuklemeAcik, setYuklemeAcik] = useState(false);
  /**
   * **E1 — HIZLI GİRİŞ SATIRININ YERİ (Tur 4½; §20.3b P1-a şerhi).**
   *
   * Soluk satır **listenin sonundadır** (P1-a) ve bu, sayfalanmayan bir
   * listede doğru yerdir. Sayfalanan listede ise satır **son sayfanın
   * sonunda** kalır: on yedi kayıtta bile ekranın altına düşer, gerçek bir
   * envanterde kullanıcı onu **hiç görmez**. Ürün sahibi bu yüzden
   * *"ekleyemiyorum"* dedi — satır oradaydı, **ulaşılamıyordu.**
   *
   * Çözüm satırı taşımak değil, **ikinci bir kapı** açmaktır: listenin
   * üstünde bir tetikleyici düğme; basılınca aynı soluk satır listenin
   * **başında** açılır. Sayfalamadan bağımsız çalışır — kullanıcı hangi
   * sayfadaysa satır orada belirir.
   *
   * ⚠️ **İKİ SOLUK SATIR AYNI ANDA ÇİZİLMEZ.** İkisi de görünseydi
   * kullanıcı hangisine yazacağını bilemez, iki taslak arasında veri
   * kaybederdi. Durum **konumu** taşır, ikinci bir satır değil.
   */
  const [hizliGirisUstte, setHizliGirisUstte] = useState(false);

  const etkinSuzgec = etkinEkipmanSuzgeciSayisi(suzgec);
  const satirlar = envanter.data?.satirlar ?? [];
  const toplam = envanter.data?.toplam ?? 0;

  /** Süzgeç değişince sayfa 1'e döner — yoksa boş sayfada kalınır. */
  function suzgeciDegistir(yama: Partial<EkipmanSuzgeci>): void {
    setSuzgec((eski) => ({ ...eski, ...yama, sayfa: 1 }));
  }

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Title order={3}>Ekipman</Title>
        <Group align="flex-end">
          <Select
            data-testid="ekipman-isyeri-suzgeci"
            label="İşyeri"
            placeholder="Tümü"
            clearable
            searchable
            value={suzgec.workplaceId}
            onChange={(deger) => suzgeciDegistir({ workplaceId: deger })}
            data={(workplaces ?? []).map((w) => ({ value: w.id, label: w.ad }))}
            w={200}
          />
          <Select
            data-testid="ekipman-tip-suzgeci"
            label="Tip"
            placeholder="Tümü"
            clearable
            searchable
            value={suzgec.tipId}
            onChange={(deger) => suzgeciDegistir({ tipId: deger })}
            data={(tipler.data ?? []).map((t) => ({ value: t.id, label: t.ad }))}
            w={200}
          />
          <Select
            data-testid="ekipman-durum-suzgeci"
            label="Kontrol durumu"
            allowDeselect={false}
            value={suzgec.kontrolDurumu ?? 'hepsi'}
            onChange={(deger) =>
              suzgeciDegistir({
                kontrolDurumu:
                  deger === 'hepsi' || deger === null ? null : (deger as EkipmanKontrolDurumu),
              })
            }
            data={[...DURUM_SUZGECI]}
            w={240}
          />
          <Select
            data-testid="ekipman-aktiflik-suzgeci"
            label="Ekipman durumu"
            allowDeselect={false}
            value={suzgec.durum ?? 'hepsi'}
            onChange={(deger) =>
              suzgeciDegistir({
                durum: deger === 'aktif' || deger === 'pasif' ? deger : null,
              })
            }
            data={[...EKIPMAN_DURUM_SUZGECI]}
            w={150}
          />
          <Select
            data-testid="ekipman-sahiplik-suzgeci"
            label="Sahiplik"
            allowDeselect={false}
            value={suzgec.sahiplik ?? 'hepsi'}
            onChange={(deger) =>
              suzgeciDegistir({
                sahiplik: deger === 'kendi' || deger === 'yuklenici' ? deger : null,
              })
            }
            data={[...SAHIPLIK_SUZGECI]}
            w={180}
          />
          <Select
            data-testid="ekipman-acil-durum-suzgeci"
            label="Acil durum"
            allowDeselect={false}
            value={suzgec.acilDurum ? 'acil' : 'hepsi'}
            onChange={(deger) => suzgeciDegistir({ acilDurum: deger === 'acil' ? true : null })}
            data={[
              { value: 'hepsi', label: 'Tüm ekipman' },
              { value: 'acil', label: 'Yalnız acil durum ekipmanı' },
            ]}
            w={220}
          />
          {yazabilir && (
            <Button data-testid="ekipman-ekle-ac" onClick={() => setHizliGirisUstte(true)}>
              + Ekipman ekle
            </Button>
          )}
          {holdingYoneticisi && (
            <Button
              variant="default"
              data-testid="ekipman-yukleme-ac"
              onClick={() => setYuklemeAcik((acik) => !acik)}
            >
              {yuklemeAcik ? 'Yükleme panelini kapat' : 'Envanteri toplu yükle'}
            </Button>
          )}
          {kapilar.data && (
            <KapiSebebi
              durum={kapilar.data.ekipmanKapisi}
              nesne="Ekipmanı"
              fiil="ekler"
              testId="ekipman-yazamama-sebebi"
            />
          )}
          {kapilar.data && (
            <KapiSebebi
              durum={kapilar.data.ekipmanYuklemeKapisi}
              nesne="Envanteri toplu"
              fiil="yükler"
              testId="ekipman-yukleme-yazamama-sebebi"
            />
          )}
          {/* ÇIKTI EKRANDAKİ SÜZGEÇLE (P4): aynı sorgu dizgisi uca gider. */}
          <Button
            variant="default"
            data-testid="ekipman-excel"
            loading={indir.isPending}
            onClick={() =>
              indir.mutate({
                yol: `/api/equipment/export${ekipmanSuzgecSorgusu(suzgec, false)}`,
                yedekAd: 'is-ekipmanlari.xlsx',
              })
            }
          >
            Excel'e aktar
          </Button>
        </Group>
      </Group>
      <QueryStatus queries={[companies, isyerleri, envanter, tipler]} />
      {holdingYoneticisi && yuklemeAcik && <EkipmanYuklemePaneli />}
      <SuzgecGostergesi
        etkinSayisi={etkinSuzgec}
        onTemizle={() => setSuzgec(BOS_EKIPMAN_SUZGECI)}
      />
      {envanter.isSuccess && toplam === 0 && !yazabilir && (
        <BosListeNotu
          mesaj={
            etkinSuzgec > 0
              ? 'Bu süzgeçlerle eşleşen ekipman yok — süzgeçleri temizleyebilirsiniz.'
              : 'Bahçenizde görüntülenecek ekipman yok.'
          }
        />
      )}
      {/* ⚠️ Yazabilen kullanıcıda BOŞ LİSTE KUTUSU ÇİZİLMEZ: ekranda yalnız
          soluk satır durur (P1'in kendi kuralı — kullanıcıya ne yapacağını
          ANLATMAK yerine yapabileceği yeri GÖSTERİRİZ). */}
      {(toplam > 0 || yazabilir) && (
        <>
          <Table striped highlightOnHover data-testid="ekipman-listesi">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ad</Table.Th>
                <Table.Th>Tip</Table.Th>
                <Table.Th>İşyeri</Table.Th>
                <Table.Th>Periyot (ay)</Table.Th>
                <Table.Th>Son kontrol</Table.Th>
                <Table.Th>Sonraki kontrol</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {/* E1 — düğmeyle açılan satır listenin BAŞINDA durur:
                  kullanıcının baktığı yer orası. */}
              {yazabilir && hizliGirisUstte && (
                <EkipmanSolukSatiri
                  isyerleri={(workplaces ?? []).map((w) => ({ id: w.id, ad: w.ad }))}
                  tipler={(tipler.data ?? []).map((t) => ({ id: t.id, ad: t.ad }))}
                  varsayilanIsyeri={suzgec.workplaceId}
                  testId="ekipman-soluk-satiri"
                />
              )}
              {satirlar.map((e) => (
                <Table.Tr key={e.id} data-testid="ekipman-satiri">
                  {/* Y1 — AD KAYDIN KENDİ SAYFASINA GÖTÜRÜR (P2: satırın
                      birincil eylemi kaydı AÇMAKTIR). */}
                  <Table.Td>
                    <Text
                      component="a"
                      href={`/ekipman/${e.id}`}
                      c={ANLAM.bilgi}
                      style={{ cursor: 'pointer' }}
                      onClick={(olay) => {
                        olay.preventDefault();
                        navigate(`/ekipman/${e.id}`);
                      }}
                      data-testid="ekipman-detay-baglantisi"
                    >
                      {e.ad}
                    </Text>
                  </Table.Td>
                  {/* K10 — ad SUNUCUDAN gelir; `find()` ile çözüm KALKTI. */}
                  <Table.Td>{e.tipAd || '—'}</Table.Td>
                  <Table.Td>
                    {(workplaces ?? []).find((w) => w.id === e.workplaceId)?.ad ?? '—'}
                  </Table.Td>
                  {/* ✏️ 91-1: periyot KAYDIN alanı değil, ÇÖZÜMLENEN değerdir. */}
                  <Table.Td>{e.kontrolPeriyoduAy ?? '—'}</Table.Td>
                  <Table.Td>{tarihGoster(e.sonKontrol)}</Table.Td>
                  <Table.Td>{tarihGoster(e.sonrakiKontrol)}</Table.Td>
                  <Table.Td>
                    <KontrolDurumRozeti durum={e.kontrolDurumu} />
                  </Table.Td>
                  <Table.Td>
                    <SatirEylemleri
                      testId="ekipman-eylemleri"
                      birincil={{
                        etiket: 'Aç',
                        to: `/ekipman/${e.id}`,
                        testId: 'ekipman-ac',
                      }}
                      digerleri={[
                        {
                          etiket: 'Periyodik görevleri',
                          onClick: () => navigate(`/ekipman/gorevler?ekipmanId=${e.id}`),
                          testId: 'ekipman-gorevleri',
                        },
                      ]}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
              {yazabilir && !hizliGirisUstte && (
                <EkipmanSolukSatiri
                  isyerleri={(workplaces ?? []).map((w) => ({ id: w.id, ad: w.ad }))}
                  tipler={(tipler.data ?? []).map((t) => ({ id: t.id, ad: t.ad }))}
                  varsayilanIsyeri={suzgec.workplaceId}
                  testId="ekipman-soluk-satiri"
                />
              )}
            </Table.Tbody>
          </Table>
          <Sayfalama
            sayfa={suzgec.sayfa}
            sayfaBoyutu={SAYFA_BOYUTU}
            toplam={toplam}
            onDegis={(sayfa) => setSuzgec((eski) => ({ ...eski, sayfa }))}
            testId="ekipman-sayfalama"
          />
          {/* ⚠️ **K9/D2 — KULLANICI YÜZEYİNDE BELGE REFERANSI DURMAZ**
              (§2.40'ın D2 kolu). Kural koda YORUM olarak yazılır, ekrana
              SONUCU yazılır. */}
          <Text size="xs" c="dimmed">
            Kontrol bitimine 1 gün kalan ekipman iş izninde seçilemez; kontrol tarihi bilinmeyen
            ekipmana sistem "uygundur" diyemez.
          </Text>
        </>
      )}
    </Stack>
  );
}

/**
 * Durum rozeti — eşik `ekipmanKontrolDurumu`nun TEK OKUYUCUSUNDAN gelir…
 * ama artık **satırın kendisi sunucudan süzülmüş** olduğu için rozet yalnız
 * gösterim yapar. Sunucu ile ekran aynı fonksiyondan konuşur
 * (`ekipmanKontrolDurumuAraligi` onun sorgu ikizidir ve birim testte
 * karşılaştırılır).
 */
function KontrolDurumRozeti({ durum }: { durum: EkipmanKontrolDurumu }): React.JSX.Element {
  // ✏️ MADDE 302: durum SUNUCUDAN (saha günü) — tarayıcı saati gün kararı vermez.
  if (durum === 'marj') {
    return (
      <Badge variant="filled" color={ANLAM.engel} data-testid="marj-rozeti">
        {EKIPMAN_KONTROL_DURUMU_ETIKETI.marj}
      </Badge>
    );
  }
  if (durum === 'yaklasiyor') {
    return (
      <Badge variant="light" color={ANLAM.dikkat} data-testid="yaklasiyor-rozeti">
        {EKIPMAN_KONTROL_DURUMU_ETIKETI.yaklasiyor}
      </Badge>
    );
  }
  if (durum === 'bilinmiyor') {
    return (
      <Badge variant="light" color={ANLAM.bilgi} data-testid="bilinmiyor-rozeti">
        {EKIPMAN_KONTROL_DURUMU_ETIKETI.bilinmiyor}
      </Badge>
    );
  }
  return (
    <Badge variant="light" color={ANLAM.olumlu} data-testid="gecerli-rozeti">
      {EKIPMAN_KONTROL_DURUMU_ETIKETI.gecerli}
    </Badge>
  );
}

/**
 * **Y2 — E1 HIZLI GİRİŞİ (madde 145).** Zorunlu minimum: işyeri · tip · ad.
 * Kalan künye kaydın kendi sayfasında tamamlanır (Y1).
 *
 * ⚠️ **SÜRE ALANI İKİNCİ KATMANDA (P1-b):** alan çoksa satır GENİŞLER, ayrı
 * sayfaya kaçılmaz. Süre `workplace_equipment_type_period` satırına gider ve
 * o işyerindeki AYNI TİPTEKİ TÜM ekipmanın vadesini belirler; bu yüzden süre
 * **zaten tanımlıysa SORULMAZ, yalnız gösterilir** (ikinci kez sormak, aynı
 * işyerinde aynı tipe iki farklı süre yazılabildiği izlenimi verirdi).
 */
function EkipmanSolukSatiri({
  isyerleri,
  tipler,
  varsayilanIsyeri,
  testId,
}: {
  isyerleri: { id: string; ad: string }[];
  tipler: { id: string; ad: string }[];
  varsayilanIsyeri: string | null;
  testId?: string;
}): React.JSX.Element {
  const ekle = useEkipmanEkle();
  const [workplaceId, setWorkplaceId] = useState<string | null>(varsayilanIsyeri);
  const [tipId, setTipId] = useState<string | null>(null);
  const [ad, setAd] = useState('');
  const [periyot, setPeriyot] = useState<number | string>('');
  const [hata, setHata] = useState<string | null>(null);
  const secilenIsyeri = workplaceId ?? varsayilanIsyeri;
  const onBilgi = useEkipmanPeriyotOnBilgi(secilenIsyeri, tipId);

  const sur = onBilgi.data;
  const sorulacak = sur !== undefined && !sur.isyeriSatiriVar && sur.yazabilir;
  const onDolu = sorulacak && periyot === '' && sur.onerilenAy !== null ? sur.onerilenAy : periyot;
  const dokunuldu = ad.trim() !== '' || tipId !== null || workplaceId !== varsayilanIsyeri;
  // P12 (madde 224) — kaydedilmemiş ekipman satırı çıkışta uyarır (yönlendirici tek noktada).
  // ⚠️ İşyeri seçimi KİRLİ SAYILMAZ: `sifirla` art arda giriş için işyerini korur —
  // kayıttan sonra satır boş ama işyeri seçili kalır; onu saymak her kayıt sonrası
  // geçişi sorardı (e2e yakaladı). Görsel soluklaşma (`dokunuldu`) ayrı eksendir.
  useKaydedilmemisIcerik(
    ad.trim() !== '' || tipId !== null || periyot !== '' ? 1 : 0,
    'ekipman satırı',
  );
  const kaydedilebilir = secilenIsyeri !== null && tipId !== null && ad.trim() !== '';

  function sifirla(): void {
    setTipId(null);
    setAd('');
    setPeriyot('');
  }

  return (
    <>
      <SolukSatir
        dokunuldu={dokunuldu}
        kaydedilebilir={kaydedilebilir}
        kaydediliyor={ekle.isPending}
        kolonSayisi={KOLON_SAYISI}
        testId={testId ?? 'ekipman-soluk-satiri'}
        onKaydet={() => {
          if (!secilenIsyeri || !tipId) return;
          setHata(null);
          ekle.mutate(
            {
              workplaceId: secilenIsyeri,
              ad: ad.trim(),
              tipId,
              // Hızlı girişte tarih SORULMAZ: künye kaydın kendi sayfasında
              // tamamlanır ve *"son kontrol"* orada bir KONTROL KAYDIDIR (K1).
              sonKontrol: null,
              // ⚠️ Satır zaten VARSA alan GÖNDERİLMEZ: aynı değeri geri
              // yollamak bile "buradan da yazabilirim" izlenimi verirdi.
              kontrolPeriyoduAy: sorulacak && onDolu !== '' ? Number(onDolu) : null,
            },
            { onSuccess: sifirla, onError: (e) => setHata(e.message) },
          );
        }}
        ikinciKatman={
          <Stack gap={4}>
            {/* ⚠️ **E2 (Tur 4½) — ALAN SATIRIN İÇİNE ALINDI, ŞERH BURADA
                KALDI.** Periyot girdisi satırın kendi *"Periyot (ay)"*
                kolonundadır (aşağıda); ikinci katman artık yalnız **sonucu
                anlatır**. Şerh SİLİNMEDİ ve silinemez: yazılan süre o
                işyerindeki **aynı tipteki TÜM** ekipmanı etkiler ve bu,
                kullanıcının satıra sayı yazarken bilmesi gereken tek şeydir
                (artçı 1c). Alanın kendisi kolona sığar; bu cümle sığmaz. */}
            {sorulacak && (
              <Text size="xs" c="dimmed" data-testid="ekipman-ekle-periyot-serhi">
                {sur.onerilenAy === null
                  ? 'Bu tip için ürün önerisi yok. Yazdığınız süre, bu işyerindeki tüm bu tip ekipmanlar için geçerli olur.'
                  : `Ürün önerisi ${sur.onerilenAy} ay. Yazdığınız süre, bu işyerindeki tüm bu tip ekipmanlar için geçerli olur.`}
              </Text>
            )}
            {sur !== undefined && sur.isyeriSatiriVar && (
              <Text size="xs" c="dimmed" data-testid="ekipman-ekle-periyot-mevcut">
                Bu işyerinde bu tipin kontrol periyodu <b>{sur.cozumlenenAy} ay</b> olarak tanımlı;
                yeni ekipman da bu süreyi alır. Değiştirmek o işyerindeki tüm bu tip ekipmanları
                etkiler — Yapılandırma ekranından yapılır.
              </Text>
            )}
            {sur !== undefined && !sur.isyeriSatiriVar && !sur.yazabilir && (
              <Text size="xs" c={ANLAM.dikkat} data-testid="ekipman-ekle-periyot-yetkisiz">
                Bu işyerinde bu tipin periyodu henüz belirlenmemiş ve bahçeniz işyerinin tamamını
                kapsamadığı için burada belirleyemezsiniz.
              </Text>
            )}
            {hata && (
              <Text size="xs" c={ANLAM.engel} role="alert" data-testid="ekipman-ekle-hata">
                {hata}
              </Text>
            )}
          </Stack>
        }
      >
        <Table.Td>
          <TextInput
            size="xs"
            placeholder="Ekipman adı"
            value={ad}
            onChange={(olay) => setAd(olay.currentTarget.value)}
            data-testid="ekipman-ekle-ad"
          />
        </Table.Td>
        <Table.Td>
          <Select
            size="xs"
            placeholder="Tip"
            searchable
            data={tipler.map((t) => ({ value: t.id, label: t.ad }))}
            value={tipId}
            onChange={(deger) => {
              setTipId(deger);
              setPeriyot('');
            }}
            data-testid="ekipman-ekle-tip"
          />
        </Table.Td>
        <Table.Td>
          <Select
            size="xs"
            placeholder="İşyeri"
            searchable
            data={isyerleri.map((w) => ({ value: w.id, label: w.ad }))}
            value={secilenIsyeri}
            onChange={(deger) => {
              setWorkplaceId(deger);
              setPeriyot('');
            }}
            data-testid="ekipman-ekle-isyeri"
          />
        </Table.Td>
        <Table.Td>
          {/* E2 — SATIR İÇİNDE. Süre zaten tanımlıysa alan ÇİZİLMEZ, değeri
              GÖSTERİLİR: ikinci kez sormak, aynı işyerinde aynı tipe iki
              farklı süre yazılabildiği izlenimi verirdi (91-2). */}
          {sorulacak ? (
            <NumberInput
              size="xs"
              w={90}
              min={1}
              allowDecimal={false}
              aria-label="Kontrol periyodu (ay)"
              placeholder="ay"
              data-testid="ekipman-ekle-periyot"
              value={onDolu}
              onChange={setPeriyot}
            />
          ) : (
            <Text size="xs" c="dimmed">
              {sur?.isyeriSatiriVar ? `${sur.cozumlenenAy} ay` : 'Tipten'}
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Text size="xs" c="dimmed">
            Kontrol geçmişinden
          </Text>
        </Table.Td>
        <Table.Td />
        <Table.Td />
      </SolukSatir>
    </>
  );
}

/**
 * **Y4 — ENVANTERİN TOPLU YÜKLENMESİ (tur 4 artçısı).**
 *
 * `DevirYuklemePaneli` deseninin ekipman kolu: **önizleme YOK** (satır kişi
 * dosyası açmaz, yanlış satır tek tıkla silinir), rapor satır numarası +
 * sebep + UYARI taşır.
 *
 * ⚠️ **UYARI ≠ HATA ve panel bunu görünür kılar.** Satır işlendiği hâlde vade
 * hesaplanamadıysa (bu işyerinde bu tipin periyodu tanımlı değil, ya da son
 * kontrol tarihi verilmemiş) satır YEŞİL değil, uyarı rengiyle listelenir —
 * kullanıcı *"neden sonraki kontrol boş"* sorusunu dosyayı kapatmadan görür.
 *
 * ⚠️ **"SON KONTROL" BEYANDIR:** yüklenen tarih künyeye yazılmaz, kontrol
 * geçmişinde **devir işaretli** bir satır doğurur (K1) — panel bunu söyler.
 */
function EkipmanYuklemePaneli(): React.JSX.Element {
  const yukle = useEkipmanYukle();
  const indir = useCiktiIndir();
  const [dosya, setDosya] = useState<File | null>(null);
  return (
    <Stack gap="sm" data-testid="ekipman-yukleme-paneli">
      <Text size="sm" c="dimmed">
        İşyerlerinin ekipman envanterini Excel'den yükler. <strong>Son kontrol tarihi</strong> bir
        künye alanı değil, sistem öncesi bir <strong>beyandır</strong>: kontrol geçmişinde “Sistem
        öncesi kayıt” rozetiyle görünür ve sonraki kontrol tarihi ondan türetilir.
      </Text>
      <Group align="flex-end">
        <Button
          variant="default"
          size="xs"
          data-testid="ekipman-sablon-indir"
          loading={indir.isPending}
          onClick={() =>
            indir.mutate({
              yol: '/api/equipment/import/template',
              yedekAd: 'is-ekipmani-sablonu.xlsx',
            })
          }
        >
          Şablonu indir
        </Button>
        <FileInput
          data-testid="ekipman-yukleme-dosya"
          placeholder="Doldurulmuş şablonu seçin (.xlsx / .csv)"
          accept=".xlsx,.csv"
          value={dosya}
          onChange={(secilen) => {
            setDosya(secilen);
            yukle.reset();
          }}
          clearable
          size="xs"
          w={300}
        />
        <Button
          size="xs"
          data-testid="ekipman-yukle"
          disabled={!dosya}
          loading={yukle.isPending}
          onClick={() => dosya && yukle.mutate({ dosya })}
        >
          Yükle
        </Button>
      </Group>
      {yukle.isError && (
        <Alert color={ANLAM.engel} role="alert" data-testid="ekipman-yukleme-hata">
          {yukle.error.message}
        </Alert>
      )}
      {yukle.data && (
        <Stack gap="xs">
          <Group gap="xs" data-testid="ekipman-yukleme-ozeti">
            <Badge color={ANLAM.olumlu} variant="light">
              Eklendi: {yukle.data.eklenen}
            </Badge>
            <Badge color={ANLAM.engel} variant="light">
              İşlenemedi: {yukle.data.hatali}
            </Badge>
          </Group>
          <Table striped data-testid="ekipman-yukleme-sonuc-tablosu">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={70}>Satır</Table.Th>
                <Table.Th w={140}>Sonuç</Table.Th>
                <Table.Th>Açıklama</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {yukle.data.satirlar.map((satir) => (
                <Table.Tr key={satir.satir} data-testid="ekipman-yukleme-sonuc-satiri">
                  <Table.Td>{satir.satir}</Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={
                        satir.sonuc === 'hata'
                          ? ANLAM.engel
                          : satir.uyarilar.length > 0
                            ? ANLAM.dikkat
                            : ANLAM.olumlu
                      }
                    >
                      {EKIPMAN_ICE_AKTARMA_SONUC_ETIKETI[satir.sonuc]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      {satir.ad && (
                        <Text size="sm" fw={500}>
                          {satir.ad}
                        </Text>
                      )}
                      {satir.mesaj && (
                        <Text size="sm" c={ANLAM.engel}>
                          {satir.mesaj}
                        </Text>
                      )}
                      {satir.uyarilar.map((uyari) => (
                        <Text key={uyari} size="xs" c={ANLAM.dikkat}>
                          {uyari}
                        </Text>
                      ))}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      )}
    </Stack>
  );
}
